import React from "react";
import { getProductChoiceError } from "../utils/productCatalog.js";
import { sortDataManagerRows } from "../utils/dataManagerTableSort.js";
import {
  ORDER_GRID_COLUMN_DEFAULT_WIDTHS,
  ORDER_GRID_RESIZABLE_COLUMNS,
  buildLoadingClientCorrespondenceIndex,
  buildOrderGridColumnStyle,
  buildVisibleCreateFormErrors,
  canDeleteOrder,
  formatDateDisplay,
  getCreateAddressValidationKey,
  getCreateOrderDeliveries,
  getNewOrderDraftErrors,
  getOrderGridTableWidth,
  hasDraftErrors,
  normalizeOptionalString,
  resolveCreateOrderDisabledMessages,
  resolveLoadingAddressPreview,
  resolveOrderGridColumnWidth,
  resolveOrderSortValue,
} from "./useDataManagerOrdersUtils.js";
import { resolveActiveOrderSearchQuery } from "../utils/dataManagerOrderSearch.js";

export function useDataManagerOrdersFilters({
  productCatalog = [],
  addressValidationErrors = {},
  addressValidationPendingKeys = {},
  browserDeliveryDate = "",
  canBulkDeleteOrders = false,
  canAssignEndOfDayLoad = false,
  canOpenConsolidation = false,
  canDeleteDeliveredOrders = false,
  currentDispatchDates = [],
  isBrowseMode = false,
  isPending = null,
  newOrderDraft = null,
  orderColumnWidths = ORDER_GRID_COLUMN_DEFAULT_WIDTHS,
  orders = [],
  searchQuery = "",
  selectedOrderIds = {},
  sortState = { direction: "asc", key: null },
  clientCorrespondences = [],
} = {}) {
  const loadingClientCorrespondenceIndex = React.useMemo(
    () => buildLoadingClientCorrespondenceIndex(clientCorrespondences),
    [clientCorrespondences],
  );
  const loadingClientOptions = loadingClientCorrespondenceIndex.options;
  const loadingClientOptionsByLookupKey = loadingClientCorrespondenceIndex.byLookupKey;
  const showBulkDeleteControls = canBulkDeleteOrders === true;
  const showOrderSelectionControls = showBulkDeleteControls || canAssignEndOfDayLoad === true || canOpenConsolidation === true;

  const sortedOrders = React.useMemo(
    () => sortDataManagerRows(orders, sortState, (order, sortKey) => resolveOrderSortValue(order, sortKey, loadingClientOptionsByLookupKey)),
    [loadingClientOptionsByLookupKey, orders, sortState],
  );

  const deletableVisibleOrders = React.useMemo(
    () => (showBulkDeleteControls ? sortedOrders.filter((order) => canDeleteOrder(order, canDeleteDeliveredOrders)) : []),
    [canDeleteDeliveredOrders, showBulkDeleteControls, sortedOrders],
  );

  const selectableVisibleOrders = React.useMemo(
    () => (showOrderSelectionControls
      ? sortedOrders.filter((order) => (
        (showBulkDeleteControls && canDeleteOrder(order, canDeleteDeliveredOrders))
        || (canAssignEndOfDayLoad === true && order?.is_editable !== false && !order?.is_cancelled)
        || (canOpenConsolidation === true && Boolean(order?.order_id))
      ))
      : []),
    [canOpenConsolidation, canAssignEndOfDayLoad, canDeleteDeliveredOrders, showBulkDeleteControls, showOrderSelectionControls, sortedOrders],
  );

  const selectedVisibleOrders = React.useMemo(
    () => selectableVisibleOrders.filter((order) => selectedOrderIds[order.order_id]),
    [selectableVisibleOrders, selectedOrderIds],
  );

  const selectedVisibleDeleteOrders = React.useMemo(
    () => deletableVisibleOrders.filter((order) => selectedOrderIds[order.order_id]),
    [deletableVisibleOrders, selectedOrderIds],
  );

  const createOrderDeliveries = React.useMemo(
    () => getCreateOrderDeliveries(newOrderDraft),
    [newOrderDraft],
  );
  const primaryCreateOrderDelivery = createOrderDeliveries[0] || null;
  const visibleOrderCount = sortedOrders.length;
  const selectedOrderCount = selectedVisibleOrders.length;
  const selectedDeleteCount = selectedVisibleDeleteOrders.length;
  const allVisibleDeletableOrdersSelected = deletableVisibleOrders.length > 0 && selectedDeleteCount === deletableVisibleOrders.length;
  const allVisibleSelectableOrdersSelected = selectableVisibleOrders.length > 0 && selectedOrderCount === selectableVisibleOrders.length;
  const cancelledOrderCount = Array.isArray(orders)
    ? orders.filter((order) => Boolean(order?.is_cancelled || order?.cancelled_at)).length
    : 0;
  const lockedOrderCount = Array.isArray(orders)
    ? orders.filter((order) => Boolean(order?.lock_reason || order?.is_editable === false)).length
    : 0;
  const createPending = typeof isPending === "function"
    ? isPending("order:create") || isPending("order:create:bulk")
    : false;
  const createAddressValidationPending = createOrderDeliveries.some((delivery) => Boolean(
    addressValidationPendingKeys[getCreateAddressValidationKey(delivery.draft_id, "address")]
  ));
  const trimmedSearchQuery = typeof searchQuery === "string" ? searchQuery.trim() : "";
  const activeSearchQuery = resolveActiveOrderSearchQuery(searchQuery);

  const effectiveNewOrderErrors = React.useMemo(() => ({
    ...getNewOrderDraftErrors(newOrderDraft, loadingClientOptionsByLookupKey),
    ...Object.fromEntries(createOrderDeliveries.flatMap((delivery, index) => {
      const error = getProductChoiceError(delivery.grain_type, productCatalog);
      return error ? [[`deliveries.${index}.grain_type`, error]] : [];
    })),
    ...Object.fromEntries(
      createOrderDeliveries.flatMap((delivery, index) => {
        const validationError = addressValidationErrors[getCreateAddressValidationKey(delivery.draft_id, "address")];
        return validationError
          ? [[`deliveries.${index}.address`, validationError]]
          : [];
      })
    ),
  }), [addressValidationErrors, createOrderDeliveries, loadingClientOptionsByLookupKey, newOrderDraft, productCatalog]);

  const visibleNewOrderErrors = React.useMemo(
    () => buildVisibleCreateFormErrors(newOrderDraft, effectiveNewOrderErrors),
    [effectiveNewOrderErrors, newOrderDraft],
  );

  const createBusy = createPending || createAddressValidationPending;
  const canCreate = !hasDraftErrors(effectiveNewOrderErrors);
  const createDisabledMessages = React.useMemo(
    () => resolveCreateOrderDisabledMessages({
      errors: effectiveNewOrderErrors,
      isPending: createPending,
      isValidatingAddress: createAddressValidationPending,
    }),
    [createAddressValidationPending, createPending, effectiveNewOrderErrors],
  );
  const createDisabledReason = React.useMemo(
    () => createDisabledMessages.join(" "),
    [createDisabledMessages],
  );
  const canApplyBrowserFilters = true;
  const createDeliveryCount = createOrderDeliveries.length;
  const createLoadingAddressPreview = resolveLoadingAddressPreview(
    newOrderDraft?.loading_client_name,
    loadingClientOptionsByLookupKey,
  );

  const createOrderConfirmationSummaryItems = React.useMemo(() => [
    {
      label: "Bon commande",
      value: "Attribué à la création",
      nowrap: true,
    },
    {
      label: "Commandes",
      value: createOrderDeliveries.length > 1
        ? `${createOrderDeliveries.length} commandes`
        : normalizeOptionalString(primaryCreateOrderDelivery?.client_name) || "À confirmer",
    },
    {
      label: "Livraison",
      value: newOrderDraft?.delivery_date ? formatDateDisplay(newOrderDraft.delivery_date) : "À planifier",
    },
    {
      label: "Nb tm",
      value: String(createOrderDeliveries.reduce((sum, delivery) => sum + (Number(delivery?.quantity_tons) || 0), 0) || 0),
    },
  ], [createOrderDeliveries.length, newOrderDraft, primaryCreateOrderDelivery?.client_name]);

  const dispatchDateSummary = React.useMemo(
    () => currentDispatchDates.length > 0
      ? currentDispatchDates.map((value) => formatDateDisplay(value)).join(" · ")
      : "Aucune date de dispatch chargée",
    [currentDispatchDates],
  );

  const isGlobalSearchMode = !isBrowseMode && activeSearchQuery.length > 0;
  const orderTableWidth = React.useMemo(
    () => getOrderGridTableWidth(orderColumnWidths, { includeSelectionColumn: showOrderSelectionControls }),
    [orderColumnWidths, showOrderSelectionControls],
  );
  const orderTableStyle = React.useMemo(() => ({
    minWidth: `${orderTableWidth}px`,
    width: `${orderTableWidth}px`,
  }), [orderTableWidth]);

  const orderTableColumnStyles = React.useMemo(
    () => Object.fromEntries(
      ORDER_GRID_RESIZABLE_COLUMNS.map((column) => [
        column.key,
        buildOrderGridColumnStyle(resolveOrderGridColumnWidth(column.key, orderColumnWidths)),
      ])
    ),
    [orderColumnWidths],
  );

  return {
    activeSearchQuery,
    allVisibleDeletableOrdersSelected,
    canApplyBrowserFilters,
    canCreate,
    cancelledOrderCount,
    createAddressValidationPending,
    createBusy,
    createDeliveryCount,
    createDisabledReason,
    createDisabledMessages,
    createLoadingAddressPreview,
    createOrderConfirmationSummaryItems,
    createOrderDeliveries,
    createPending,
    deletableVisibleOrders,
    dispatchDateSummary,
    effectiveNewOrderErrors,
    isGlobalSearchMode,
    loadingClientCorrespondenceIndex,
    loadingClientOptions,
    loadingClientOptionsByLookupKey,
    lockedOrderCount,
    orderTableColumnStyles,
    orderTableStyle,
    orderTableWidth,
    primaryCreateOrderDelivery,
    selectedDeleteCount,
    selectedOrderCount,
    selectedVisibleOrders,
    selectedVisibleDeleteOrders,
    selectableVisibleOrders,
    showOrderSelectionControls,
    showBulkDeleteControls,
    allVisibleSelectableOrdersSelected,
    sortedOrders,
    trimmedSearchQuery,
    visibleNewOrderErrors,
    visibleOrderCount,
  };
}

export default useDataManagerOrdersFilters;
